import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUser } from "selectors/commonSelectors";
import { hasApprovalWorkflows } from "utils/feature-gates";
import { Button } from "components/ui/button";
import { queryKeys } from "api/query-keys";
import { queryRunForPost, workflowError, workflowUrl } from "./workflow-api";
import type { RunProjection } from "./types";

export function WorkflowProgress({
  run,
  compact = false,
}: {
  run: RunProjection;
  compact?: boolean;
}) {
  const active = run.state === "starting" || run.state === "pending";
  if (compact)
    return (
      <span
        className="max-w-28 truncate rounded bg-blue-50 px-1 text-[10px]"
        title={`Step ${run.stepIndex + 1} of ${run.stepTotal} — ${run.stepLabel}; awaiting ${run.pendingActorCount} reviewers`}
      >
        Step {run.stepIndex + 1}/{run.stepTotal} ·{" "}
        {active ? `${run.pendingActorCount} waiting` : run.state}
      </span>
    );
  return (
    <span role="status" className="block text-xs leading-5 text-neutral-700">
      {active
        ? `Step ${run.stepIndex + 1} of ${run.stepTotal} — ${run.stepLabel}`
        : `Workflow ${run.state}`}
      {active && (
        <span className="block text-neutral-500">
          {run.state === "starting"
            ? "Starting review…"
            : `Awaiting ${run.pendingActorCount} reviewer${run.pendingActorCount === 1 ? "" : "s"} across ${run.pendingLinkCount} link${run.pendingLinkCount === 1 ? "" : "s"}`}
        </span>
      )}
    </span>
  );
}

export function WorkflowRunStatus({
  postId,
  controls = false,
  compact = false,
}: {
  postId?: string;
  controls?: boolean;
  compact?: boolean;
}) {
  const user = useSelector(getUser);
  if (!postId || !user || !hasApprovalWorkflows(user)) return null;
  return (
    <ConnectedWorkflowRunStatus
      compact={compact}
      postId={postId}
      controls={controls && user.role === "admin"}
      tenantId={user.adminId || user._id}
    />
  );
}

function ConnectedWorkflowRunStatus({
  postId,
  controls,
  tenantId,
  compact,
}: {
  postId: string;
  controls: boolean;
  tenantId?: string;
  compact: boolean;
}) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["approval-run", tenantId, postId],
    queryFn: () => queryRunForPost(tenantId ?? "", postId),
    refetchInterval: 15000,
    staleTime: 10000,
  });
  // A completed projection can predate a later manual status change. Refresh
  // current post data on transitions instead of copying historical status.
  const runVersion = query.data
    ? `${query.data.runId}:${query.data.revision}:${query.data.state}`
    : undefined;
  const previousRunVersion = useRef(runVersion);
  useEffect(() => {
    if (
      previousRunVersion.current &&
      runVersion &&
      previousRunVersion.current !== runVersion
    ) {
      client.invalidateQueries({
        queryKey: queryKeys.approvals.boardPosts.all,
      });
      client.invalidateQueries({ queryKey: queryKeys.calendar.all });
      client.invalidateQueries({ queryKey: queryKeys.posts.all });
    }
    previousRunVersion.current = runVersion;
  }, [client, runVersion]);
  const [action, setAction] = useState<"cancel" | "forceComplete" | null>(null);
  const [reason, setReason] = useState("");
  useEffect(() => {
    setAction(null);
    setReason("");
  }, [query.data?.runId, query.data?.revision]);
  const command = useRef<{ key: string; requestId: string } | null>(null);
  const mutation = useMutation({
    mutationFn: async ({
      run,
      action,
      reason,
    }: {
      run: RunProjection;
      action: "cancel" | "forceComplete";
      reason: string;
    }) => {
      const key = JSON.stringify([run.runId, run.revision, action, reason]);
      if (command.current?.key !== key)
        command.current = { key, requestId: crypto.randomUUID() };
      return (
        await axios.post<{ run: RunProjection }>(
          `${workflowUrl}/runs/${encodeURIComponent(run.runId)}/override`,
          {
            action,
            reason,
            expectedRevision: run.revision,
            requestId: command.current.requestId,
          }
        )
      ).data.run;
    },
    onSuccess: (run) => {
      client.setQueryData(["approval-run", tenantId, postId], run);
      setAction(null);
      setReason("");
      client.invalidateQueries({
        queryKey: queryKeys.approvals.boardPosts.all,
      });
      client.invalidateQueries({ queryKey: queryKeys.calendar.all });
      client.invalidateQueries({ queryKey: queryKeys.posts.all });
    },
    onSettled: () => {
      query.refetch();
    },
  });
  if (query.isLoading)
    return compact ? null : (
      <span role="status" className="text-xs text-neutral-500">
        Checking workflow…
      </span>
    );
  if (query.error)
    return (
      <span role="alert" className="text-xs">
        Workflow progress unavailable.{" "}
        <button
          type="button"
          className="underline"
          onClick={(event) => {
            event.stopPropagation();
            query.refetch();
          }}
        >
          Retry
        </button>
      </span>
    );
  const run = query.data;
  if (!run) return null;
  const active = run.state === "pending" || run.state === "starting";
  return (
    <div className="min-w-0" onClick={(event) => event.stopPropagation()}>
      <WorkflowProgress run={run} compact={compact} />
      {controls && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer">
            Workflow details · v{run.workflowVersion}
          </summary>
          <ul className="my-2 space-y-1">
            {run.links.map((link, index) => (
              <li key={link.linkId}>
                Link {index + 1}: {link.approvalCount}/{link.requiredApprovals}{" "}
                people approved · {link.state}
              </li>
            ))}
          </ul>
          <ul className="my-2 space-y-1">
            {run.audit.map((entry) => (
              <li key={entry.id}>
                <time dateTime={entry.at}>
                  {new Date(entry.at).toLocaleString()}
                </time>{" "}
                · {entry.action}
                {entry.reason ? ` — ${entry.reason}` : ""}
              </li>
            ))}
          </ul>
          {active && !action && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  mutation.reset();
                  setAction("cancel");
                }}
              >
                Cancel run
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  mutation.reset();
                  setAction("forceComplete");
                }}
              >
                Force complete
              </Button>
            </div>
          )}
          {action && active && (
            <form
              className="mt-3 space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (reason.trim())
                  mutation.mutate({ run, action, reason: reason.trim() });
              }}
            >
              <p>
                {action === "cancel"
                  ? "Cancel this run and keep its held pending status."
                  : "Finish this run and move the post to its approved status."}{" "}
                Your reason will be recorded in the audit history.
              </p>
              <label className="block">
                Audit reason
                <textarea
                  aria-label="Audit reason"
                  className="mt-1 w-full rounded border p-2"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  required
                  maxLength={200}
                  disabled={mutation.isPending}
                />
              </label>
              {mutation.error && (
                <p role="alert" className="text-red-700">
                  {workflowError(mutation.error)}
                </p>
              )}
              <Button
                type="submit"
                size="sm"
                disabled={
                  !reason.trim() || mutation.isPending || query.isFetching
                }
              >
                {mutation.isPending
                  ? "Applying…"
                  : action === "cancel"
                    ? "Confirm cancellation"
                    : "Confirm force complete"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => setAction(null)}
              >
                Back
              </Button>
            </form>
          )}
        </details>
      )}
    </div>
  );
}
